// Validation utility functions for doctor onboarding

import {
    basicInfoSchema,
    professionalDetailsSchema,
    documentVerificationSchema,
    practiceSetupSchema,
    paymentAndSubscriptionSchema,
} from './validationSchemas';
import { validateAndNormalizePhone } from './phoneValidation';

// Helper function to format Zod errors into a flat object
const formatZodErrors = (error) => {
    if (!error) return {};
    return error.errors.reduce((acc, curr) => {
        const path = curr.path.join('.');
        acc[path] = curr.message;
        return acc;
    }, {});
};

// Email validation
export const validateEmail = (email) => {
    if (!email) return 'Email is required';

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
        return 'Please enter a valid email address';
    }

    // Check for common typos in domain
    const domain = email.split('@')[1];
    const commonTypos = {
        'gmail.com': ['gamil.com', 'gmial.com', 'gmal.com'],
        'yahoo.com': ['yaho.com', 'yahooo.com'],
        'hotmail.com': ['hotmal.com', 'hotmai.com'],
        'outlook.com': ['outlok.com', 'outlock.com'],
    };

    for (const [correct, typos] of Object.entries(commonTypos)) {
        if (typos.includes(domain)) {
            return `Did you mean ${correct}?`;
        }
    }

    return null;
};

// Phone number validation using enhanced validation

export const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';

    const validationResult = validateAndNormalizePhone(phone);

    if (!validationResult.isValid) {
        return validationResult.error;
    }

    return null;
};

// Name validation
export const validateName = (name) => {
    if (!name) return 'Full name is required';

    // Check minimum length
    if (name.length < 2) {
        return 'Name must be at least 2 characters long';
    }

    // Check maximum length
    if (name.length > 100) {
        return 'Name must not exceed 100 characters';
    }

    // Check for valid characters (letters, spaces, and common name characters)
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name)) {
        return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }

    // Check for multiple spaces
    if (name.includes('  ')) {
        return 'Name cannot contain multiple consecutive spaces';
    }

    return null;
};

// Address validation
export const validateAddress = (address) => {
    if (!address) return 'Address is required';

    // Check minimum length
    if (address.length < 5) {
        return 'Address must be at least 5 characters long';
    }

    // Check maximum length
    if (address.length > 500) {
        return 'Address must not exceed 500 characters';
    }

    return null;
};

// Date of birth validation
export const validateDateOfBirth = (date) => {
    if (!date) return 'Date of birth is required';

    // Handle both Date objects and date strings
    const birthDate = date instanceof Date ? date : new Date(date);
    const today = new Date();

    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
        return 'Please enter a valid date';
    }

    // Check if date is in the future
    if (birthDate > today) {
        return 'Date of birth cannot be in the future';
    }

    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    // Check minimum age (25 years)
    if (age < 25) {
        return 'You must be at least 25 years old to register as a doctor';
    }

    // Check maximum age (reasonable limit of 100 years)
    if (age > 100) {
        return 'Please enter a valid date of birth';
    }

    return null;
};

// Gender validation
export const validateGender = (gender) => {
    if (!gender) return 'Gender is required';

    const validGenders = ['male', 'female', 'other'];
    if (!validGenders.includes(gender)) {
        return 'Please select a valid gender option';
    }

    return null;
};

// Basic Information Validation
export const validateBasicInfo = (data) => {
    try {
        // Log the data being validated for debugging
        console.log('Validating basic info:', JSON.stringify(data, null, 2));

        // Initialize errors object
        let errors = {};

        // Ensure all required fields are present
        const requiredFields = ['fullName', 'email', 'phone', 'gender', 'dateOfBirth', 'address'];
        const missingFields = requiredFields.filter(field => !data[field]);

        if (missingFields.length > 0) {
            errors = missingFields.reduce((acc, field) => {
                acc[field] = `${field.replace(/([A-Z])/g, ' $1').trim()} is required`;
                return acc;
            }, {});
        }

        // Validate email format
        if (data.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(data.email)) {
            errors.email = 'Please enter a valid email address';
        }

        // Validate phone number format
        if (data.phone) {
            // Remove all non-digit characters
            const cleanPhone = data.phone.replace(/\D/g, '');

            // Check if we have between 10 and 15 digits
            if (cleanPhone.length < 10 || cleanPhone.length > 15) {
                errors.phone = 'Please enter a valid phone number (10-15 digits)';
            }
        }

        // Validate date of birth
        if (data.dateOfBirth) {
            let dob;
            try {
                // Handle both string and Date objects
                dob = data.dateOfBirth instanceof Date ? data.dateOfBirth : new Date(data.dateOfBirth);

                // Check if the date is valid
                if (isNaN(dob.getTime())) {
                    errors.dateOfBirth = 'Please enter a valid date of birth';
                } else {
                    const today = new Date();
                    const age = today.getFullYear() - dob.getFullYear();
                    const monthDiff = today.getMonth() - dob.getMonth();

                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                        age--;
                    }

                    if (age < 25) {
                        errors.dateOfBirth = 'You must be at least 25 years old to register as a doctor';
                    }

                    // Check if date is in the future
                    if (dob > today) {
                        errors.dateOfBirth = 'Date of birth cannot be in the future';
                    }
                }
            } catch (error) {
                errors.dateOfBirth = 'Please enter a valid date of birth';
            }
        }

        // Validate gender
        if (data.gender && !['male', 'female', 'other'].includes(data.gender.toLowerCase())) {
            errors.gender = 'Please select a valid gender option';
        }

        // Validate address
        if (data.address && data.address.trim().length < 10) {
            errors.address = 'Please enter a complete address (minimum 10 characters)';
        }

        // If there are any errors, return them
        if (Object.keys(errors).length > 0) {
            console.log('Validation errors:', errors);
            return errors;
        }

        // Use the Zod schema for validation
        basicInfoSchema.parse(data);
        return {};
    } catch (error) {
        console.error('Validation error:', error);
        return formatZodErrors(error);
    }
};

// Professional Details Validation
export const validateProfessionalDetails = (data) => {
    try {
        professionalDetailsSchema.parse(data);
        return {};
    } catch (error) {
        return formatZodErrors(error);
    }
};

// Document Verification Validation
export const validateDocuments = (data, professionalType) => {
    try {
        documentVerificationSchema.parse({...data, professionalType });
        return {};
    } catch (error) {
        return formatZodErrors(error);
    }
};

// Practice Setup Validation
export const validatePracticeSetup = (data) => {
    try {
        practiceSetupSchema.parse(data);
        return {};
    } catch (error) {
        return formatZodErrors(error);
    }
};

// Payment and Subscription Validation
export const validatePaymentAndSubscription = (data, subscription) => {
    try {
        paymentAndSubscriptionSchema.parse({...data, subscription });
        return {};
    } catch (error) {
        return formatZodErrors(error);
    }
};

// Field validation helper
export const validateField = (field, value) => {
    switch (field) {
        case 'fullName':
            return validateName(value);
        case 'email':
            return validateEmail(value);
        case 'phone':
            return validatePhone(value);
        case 'gender':
            return validateGender(value);
        case 'date_of_birth':
            return validateDateOfBirth(value);
        case 'address':
            return validateAddress(value);
        default:
            return null;
    }
};

// Export schemas for field-level validation
export {
    basicInfoSchema,
    professionalDetailsSchema,
    documentVerificationSchema,
    practiceSetupSchema,
    paymentAndSubscriptionSchema,
};